import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, Modal } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import CustomForm from '../../../components/FormRenderer/CustomForm';

import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';

import { currencyOptions } from '../../../utils/constants';
import {
  addNewBuzzSubscriptionPlan,
  getAllBuzzSubscriptionPlans,
  getAllBuzzSubscriptionProducts,
  updateBuzzSubscriptionPlan,
} from '../BuzzSlice';
import usePlanOptions from '../usePlanOptions';
import BuzzSelect from '../../../components/BuzzSelect/BuzzSelect';

// A SkillUp plan (either category buzzOne flags `skillUp`: for schools, or for individuals)
// names its course in the LMS: `courseId` is required, `vendorId` optional.
// The form reads flat slugs, so these live as `metadata.*` keys while editing.
const skillUpFields = [
  {
    id: 'form.metadata.courseId',
    title: 'Course Id',
    slug: 'metadata.courseId',
    type: 'String',
    required: true,
  },
  {
    id: 'form.metadata.vendorId',
    title: 'Vendor Id (optional)',
    slug: 'metadata.vendorId',
    type: 'String',
  },
];

export default function BuzzSubscriptionPlans() {
  const dispatch = useDispatch();
  const { handleLoader } = useLoader();
  const { SuccessToast, ErrorToast } = useToast();
  const {
    ecosystems: userTypeOptions,
    productCategories: productCategoryOptions,
    licenseQuotaTypes: licenseQuotaTypeOptions,
  } = usePlanOptions();
  const isSkillUp = (category) =>
    productCategoryOptions.some(
      ({ value, skillUp }) => value === category && skillUp
    );

  const { subscriptionPlans, subscriptionProducts } = useSelector(
    ({ buzz }) => {
      return {
        subscriptionPlans: buzz.subscriptionPlans,
        subscriptionProducts: buzz.subscriptionProducts || [],
      };
    }
  );

  const [ecosystem, setEcosystem] = useState(null);

  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({
    ecosystem: null,
  });

  useEffect(() => {
    dispatch(getAllBuzzSubscriptionPlans({ ecosystem }));
    dispatch(getAllBuzzSubscriptionProducts({ ecosystem }));
  }, [ecosystem]);

  useEffect(() => {
    setEcosystem(() => formData.ecosystem);
  }, [formData.ecosystem]);

  useEffect(() => {
    setFormData((state) => {
      state['ecosystem'] = ecosystem;
      return state;
    });
  }, [ecosystem]);

  useEffect(() => {
    if (selectedRowId) {
      let selectedPlan = JSON.parse(
        JSON.stringify(
          subscriptionPlans.filter((item) => item._id === selectedRowId)[0]
        )
      );
      const { metadata, ...plan } = selectedPlan;
      setFormData({
        ...plan,
        'metadata.courseId': metadata && metadata.courseId,
        'metadata.vendorId': metadata && metadata.vendorId,
      });
    } else setFormData({});
  }, [selectedRowId]);

  useEffect(() => {
    if (!subscriptionProducts) return;

    const buzzProducts = subscriptionProducts.map((product) => {
      return { label: product, value: product };
    });

    const formTemplate = [
      {
        id: 'form.ecosystem',
        title: 'Ecosystem',
        slug: 'ecosystem',
        type: 'Select',
        options: userTypeOptions,
        required: true,
      },
      {
        id: 'form.name',
        title: 'Plan Name',
        slug: 'name',
        type: 'String',
        required: true,
      },
      {
        id: 'form.productCategory',
        title: 'Product Category',
        slug: 'productCategory',
        type: 'AutoCompleteSelect',
        options: productCategoryOptions,
        required: true,
      },
      {
        id: 'form.products',
        title: 'Products',
        slug: 'products',
        type: 'AutoCompleteMultiSelect',
        options: buzzProducts,
        freeSolo: true,
        required: true,
      },
      {
        id: 'form.variants',
        title: 'Plan Variants',
        slug: 'variants',
        type: 'Array',

        arrayElementType: [
          {
            id: 'form.variants.durationInMonth',
            title: 'Durations (months)',
            slug: 'durationInMonth',
            type: 'Number',
            required: true,
          },
          {
            id: 'form.variants.priceDetails',
            title: 'Price Details',
            slug: 'priceDetails',
            type: 'Array',
            arrayElementType: [
              {
                id: 'form.variants.priceDetails.currency',
                title: 'Currency',
                slug: 'currency',
                type: 'AutoCompleteSelect',
                options: currencyOptions,
                required: true,
              },
              {
                id: 'form.variants.priceDetails.subscriptionOneTimeFee',
                title: 'Subscription OneTime Fee',
                slug: 'subscriptionOneTimeFee',
                type: 'FloatNumber',
                required: true,
              },
              {
                id: 'form.variants.priceDetails.subscriptionPricePerMonth',
                title: 'Subscription Price (per Month)',
                slug: 'subscriptionPricePerMonth',
                type: 'FloatNumber',
                required: true,
              },
              {
                id: 'form.variants.priceDetails.licensePriceDetails',
                title: 'License Price Details',
                slug: 'licensePriceDetails',
                type: 'Array',
                arrayElementType: [
                  {
                    id: 'form.variants.priceDetails.licensePriceDetails.licenseQuotaType',
                    title: 'License Type',
                    slug: 'licenseQuotaType',
                    type: 'Select',
                    options: licenseQuotaTypeOptions,
                  },
                  {
                    id: 'form.variants.priceDetails.licensePriceDetails.pricePerMonth',
                    title: 'License Price (per Month)',
                    slug: 'pricePerMonth',
                    type: 'FloatNumber',
                  },
                ],
                required: true,
              },
            ],
          },
        ],
        required: true,
      },
    ];
    setFormTemplate(formTemplate);
  }, [subscriptionProducts, userTypeOptions]);

  const subscriptionPlanColumns = [
    {
      Header: 'Name',
      accessor: 'name',
    },
    {
      Header: 'Category',
      accessor: 'productCategory',
    },
    {
      Header: 'Products',
      Cell: (val) => {
        const products = val.cell.row.original.products;
        const plans = val.cell.row.original.plans;
        if (products) return <> {products.join(', ')}</>;
        else if (plans) return <> {plans.join(', ')}</>;

        return <></>;
      },
    },
    {
      Header: 'Duration (months)',
      Cell: (val) => {
        const planVariants = val.cell.row.original.variants;

        return (
          <>
            {planVariants
              .map(({ durationInMonth }) => durationInMonth)
              .join(', ')}
          </>
        );

        return <></>;
      },
    },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;

        return (
          <>
            <IconButton onClick={() => handleEdit(_id)}>
              <EditIcon color="secondary" />
            </IconButton>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    openFormModal();
  };

  const handleSave = async () => {
    const {
      'metadata.courseId': courseId,
      'metadata.vendorId': vendorId,
      ...payload
    } = formData;
    // The API refuses metadata on any other category.
    if (isSkillUp(payload.productCategory))
      payload.metadata = {
        courseId: courseId && courseId.trim(),
        ...(vendorId && vendorId.trim() && { vendorId: vendorId.trim() }),
      };

    dispatch(
      selectedRowId === null
        ? addNewBuzzSubscriptionPlan(payload)
        : updateBuzzSubscriptionPlan({
            id: selectedRowId,
            data: payload,
          })
    )
      .unwrap()
      .then((res) => {
        SuccessToast(res.message);
        dispatch(getAllBuzzSubscriptionPlans({ ecosystem }));
        closeFormModal();
        setFormData({});
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box
        display="flex"
        alignItems={'center'}
        justifyContent={'space-between'}
        style={{ gap: '1rem', width: '100%' }}>
        <div>
          <Button
            onClick={handleCreateNew}
            variant={'contained'}
            color="primary">
            Add New Plan
          </Button>
        </div>

        <div style={{ minWidth: '20%' }}>
          <BuzzSelect
            // fullWidth={false}
            name="Ecosystem"
            size={'small'}
            placeholder={'Select Ecosystem'}
            value={ecosystem}
            onChange={(e) => {
              setEcosystem(e.target.value);
            }}
            // label="Select Type"
            // valueKey="_id"
            // labelKey="name"
            options={[
              { label: 'All Ecosystem', value: undefined },
              ...userTypeOptions,
            ]}
          />
        </div>
      </Box>
      <Box my={2}>
        <BuzzTable
          columns={subscriptionPlanColumns}
          data={subscriptionPlans}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={() => setFormModal(false)}>
        <>
          {' '}
          <CustomForm
            formFor={'Product Plan'}
            formTemplate={
              formTemplate && isSkillUp(formData.productCategory)
                ? formTemplate.flatMap((field) =>
                    field.slug === 'productCategory'
                      ? [field, ...skillUpFields]
                      : field.slug === 'products'
                      ? // Fixed to SKILLUP_COURSE — the course is `courseId`, not a product.
                        [{ ...field, disabled: true }]
                      : [field]
                  )
                : formTemplate
            }
            formData={formData}
            setFormData={(slug, newValue) => {
              setFormData((state) => {
                state[slug] = newValue;
                // A SkillUp plan sells exactly one product; leaving SkillUp frees the field.
                if (slug === 'productCategory' && isSkillUp(newValue))
                  state.products = ['SKILLUP_COURSE'];
                else if (
                  slug === 'productCategory' &&
                  state.products &&
                  state.products.includes('SKILLUP_COURSE')
                )
                  state.products = [];
                return state;
              });
              setFormTemplate((state) => {
                return JSON.parse(JSON.stringify(state));
              });
            }}
            mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
            handleSave={handleSave}
          />
        </>
      </Modal>
    </Box>
  );
}
